import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useForm, SubmitHandler } from 'react-hook-form';
import { toast } from 'react-toastify';
import { TradeSet, Preset, TradeSetPresetLink } from '../../types/preset';
import { tradeSetService } from '../../services/tradeSetService';
import { presetService } from '../../services/presetService';
import { Button } from '../common/Button';
import { Input } from '../common/Input';

// Basic Modal Styling
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #2a2a2a; // Dark background
  padding: 2rem;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  border: 1px solid #444;
`;

const ModalHeader = styled.h2`
  margin-top: 0;
  margin-bottom: 1.5rem;
  color: #e5e7eb; // Light text
  font-size: 1.25rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: #9ca3af; // Lighter gray text
  font-size: 0.875rem;
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1.5rem;
`;

const StyledTextarea = styled.textarea`
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #4b5563; // Gray border
  border-radius: 4px;
  background-color: #374151; // Darker background
  color: #e5e7eb; // Light text
  font-size: 0.875rem;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #3b82f6; // Blue border on focus
    box-shadow: 0 0 0 1px #3b82f6;
  }

  &::placeholder {
    color: #6b7280; // Lighter placeholder text
  }
`;

const PresetListContainer = styled.div`
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #4b5563;
  border-radius: 4px;
  padding: 0.5rem;
  margin-top: 0.5rem;
  background-color: #1f2937; // Slightly lighter dark background
`;

const PresetListItem = styled.div`
  display: flex;
  align-items: center;
  padding: 0.3rem 0.5rem;
  margin-bottom: 2px;
  border-radius: 3px;
  cursor: pointer;
  color: #d1d5db; // Lighter text

  &:hover {
    background-color: #374151;
  }

  label {
    margin-left: 0.5rem;
    flex-grow: 1;
  }
`;

const CheckboxInput = styled.input.attrs({ type: 'checkbox' })`
  cursor: pointer;
`;

const OrderInput = styled.input.attrs({ type: 'number' })`
  width: 60px;
  margin-left: auto;
  text-align: center;
  padding: 2px 5px;
  background-color: #374151;
  border: 1px solid #4b5563;
  border-radius: 3px;
  color: #e5e7eb;
`;

// Status indicators
const LoadingText = styled.div`
  color: #9ca3af;
  text-align: center;
  padding: 10px 0;
`;

const ErrorText = styled.div`
  color: #ef4444;
  text-align: center;
  padding: 10px 0;
`;

const EmptyText = styled.div`
  color: #9ca3af;
  text-align: center;
  padding: 10px 0;
  font-style: italic;
`;

interface EditTradeSetFormData {
  name: string;
  description: string;
  webhook_enabled: boolean;
  webhook_id: string;
}

interface EditTradeSetModalProps {
  isOpen: boolean;
  onClose: () => void;
  tradeSet: TradeSet | null;
  onTradeSetUpdated: (updatedTradeSet: TradeSet) => void;
}

interface PresetWithLinkStatus extends Preset {
  isLinked: boolean;
  order: number;
}

const EditTradeSetModal: React.FC<EditTradeSetModalProps> = ({
  isOpen,
  onClose,
  tradeSet,
  onTradeSetUpdated,
}) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<EditTradeSetFormData>();

  // State for available presets
  const [availablePresets, setAvailablePresets] = useState<Preset[]>([]);
  const [loadingPresets, setLoadingPresets] = useState(false);
  const [errorPresets, setErrorPresets] = useState<string | null>(null);
  
  // State for linked presets with additional UI info
  const [linkedPresets, setLinkedPresets] = useState<Map<string, number>>(new Map());
  
  // Reset form and fetch available presets when tradeSet data changes
  useEffect(() => {
    if (tradeSet && isOpen) {
      // Reset basic fields
      reset({
        name: tradeSet.name,
        description: tradeSet.description || '',
        webhook_enabled: tradeSet.webhook_enabled,
        webhook_id: tradeSet.webhook_id || '',
      });
      
      // Initialize linked presets map (preset_id -> order)
      const initialLinkedPresets = new Map<string, number>();
      
      // Get preset links from tradeSet
      if (Array.isArray(tradeSet.presets)) {
        tradeSet.presets.forEach((preset, index) => {
          // Find the corresponding preset link order
          const order = index + 1; // Use index + 1 as default order
          initialLinkedPresets.set(preset.id, order);
        });
      }
      
      setLinkedPresets(initialLinkedPresets);

      // Fetch available presets for this symbol
      const fetchAvailablePresets = async () => {
        setLoadingPresets(true);
        setErrorPresets(null);
        try {
          // Fetch presets filtered by symbol
          const response = await presetService.getPresets(1, 1000, undefined, tradeSet.symbol);
          setAvailablePresets(response.items);
        } catch (err) {
          console.error('Error fetching available presets:', err);
          setErrorPresets('Failed to load available presets.');
          setAvailablePresets([]);
        } finally {
          setLoadingPresets(false);
        }
      };

      fetchAvailablePresets();
    } else {
      // Reset form and state when modal closes
      reset({ 
        name: '', 
        description: '',
        webhook_enabled: false,
        webhook_id: ''
      });
      setLinkedPresets(new Map());
      setAvailablePresets([]);
    }
  }, [tradeSet, isOpen, reset]);

  // Handler for checkbox changes
  const handlePresetLinkChange = (presetId: string) => {
    setLinkedPresets(prev => {
      const newLinkedPresets = new Map(prev);
      
      if (newLinkedPresets.has(presetId)) {
        // If already linked, remove it
        newLinkedPresets.delete(presetId);
      } else {
        // Add with the next available order
        const maxOrder = prev.size > 0 
          ? Math.max(...Array.from(prev.values())) 
          : 0;
        newLinkedPresets.set(presetId, maxOrder + 1);
      }
      
      return newLinkedPresets;
    });
  };

  // Handler for order changes
  const handleOrderChange = (presetId: string, newOrder: number) => {
    setLinkedPresets(prev => {
      const newLinkedPresets = new Map(prev);
      if (newLinkedPresets.has(presetId)) {
        newLinkedPresets.set(presetId, newOrder);
      }
      return newLinkedPresets;
    });
  };

  const onSubmit: SubmitHandler<EditTradeSetFormData> = async (formData) => {
    if (!tradeSet) {
      toast.error('No TradeSet selected for editing.');
      return;
    }

    // Convert linked presets to the format expected by the API
    const presetLinks: TradeSetPresetLink[] = Array.from(linkedPresets.entries())
      .map(([preset_id, order]) => ({
        preset_id, // preset_id is already a string
        order
      }));

    // Prepare the update payload
    const updatePayload = {
      name: formData.name,
      description: formData.description,
      webhook_enabled: formData.webhook_enabled,
      webhook_id: formData.webhook_id,
      preset_links: presetLinks,
    };

    try {
      // Update the tradeSet
      const updatedTradeSet = await tradeSetService.updateTradeSet(tradeSet.id.toString(), updatePayload);
      
      toast.success(`TradeSet "${updatedTradeSet.name}" updated successfully.`);
      onTradeSetUpdated(updatedTradeSet);
      onClose();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.detail || error.message || 'Failed to update TradeSet.';
      toast.error(errorMessage);
      console.error('Error updating TradeSet:', error);
    }
  };

  if (!isOpen || !tradeSet) {
    return null;
  }

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>Edit TradeSet: {tradeSet.name} ({tradeSet.symbol})</ModalHeader>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <div>
            <Label htmlFor="tradeSetName">Name</Label>
            <Input
              id="tradeSetName"
              {...register('name', { required: 'Name is required' })}
              placeholder="Enter TradeSet name"
            />
            {errors.name && <ErrorText>{errors.name.message}</ErrorText>}
          </div>

          <div>
            <Label htmlFor="tradeSetDescription">Description</Label>
            <StyledTextarea
              id="tradeSetDescription"
              {...register('description')}
              placeholder="Optional description"
              rows={3}
            />
          </div>

          <div>
            <Label htmlFor="webhookEnabled">Webhook Settings</Label>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
              <CheckboxInput
                id="webhookEnabled"
                {...register('webhook_enabled')}
              />
              <label htmlFor="webhookEnabled" style={{ marginLeft: '0.5rem' }}>Enable Webhook</label>
            </div>
            
            <Input
              id="webhookId"
              {...register('webhook_id')}
              placeholder="Webhook ID (optional)"
            />
          </div>

          {/* Linked Presets Section */}
          <div>
            <Label>Linked presets ({linkedPresets.size})</Label>
            {loadingPresets ? (
              <LoadingText>Loading available presets...</LoadingText>
            ) : errorPresets ? (
              <ErrorText>{errorPresets}</ErrorText>
            ) : availablePresets.length === 0 ? (
              <EmptyText>No presets available for {tradeSet.symbol}</EmptyText>
            ) : (
              <PresetListContainer>
                {availablePresets.map((preset) => {
                  const isLinked = linkedPresets.has(preset.id);
                  const order = linkedPresets.get(preset.id) || 0;
                  
                  return (
                    <PresetListItem key={preset.id}>
                      <CheckboxInput
                        id={`preset-${preset.id}`}
                        checked={isLinked}
                        onChange={() => handlePresetLinkChange(preset.id)}
                      />
                      <label htmlFor={`preset-${preset.id}`}>
                        {preset.name}
                      </label>
                      
                      {isLinked && (
                        <OrderInput
                          value={order}
                          onChange={(e) => handleOrderChange(preset.id, parseInt(e.target.value, 10) || 0)}
                          min={0}
                        />
                      )}
                    </PresetListItem>
                  );
                })}
              </PresetListContainer>
            )}
          </div>

          <ButtonGroup>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </ButtonGroup>
        </Form>
      </ModalContent>
    </ModalOverlay>
  );
};

export default EditTradeSetModal; 